import { useState, createContext, useContext, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { showMessage as sliceShowMessage } from 'app/store/fuse/messageSlice';
import { useTranslation } from 'react-i18next';
import { generateId } from 'app/admin/whatIf/component/WhatIfUtil';
import _ from 'lodash';

const slideSensitivity = 20;
const jumpSensitivity = 25;
const cornerPercent = 25;

export const WorkflowContext = createContext(null);

export const useWorkflow = () => {
	return useContext(WorkflowContext);
};

export default function WorkflowProvider(props) {
	const { isTransformer = false } = props;
	const dispatch = useDispatch();
	const { t } = useTranslation('transformer');
	const [stage, setStage] = useState(null);
	const [data, setData] = useState(null);
	const [editMode, setEditMode] = useState('MOVE');
	const [showPropertiesDialog, setShowPropertiesDialog] = useState(null);
	const [underEditing, setUnderEditing] = useState(null);
	const [selected, setSelected] = useState([]);
	const [selectedCalculate, setSelectedCalculate] = useState(null);
	const [floatCalculate, setFloatCalculate] = useState(null);
	const [floatValidation, setFloatValidation] = useState(null);
	const [calculate, setCalculate] = useState(null);

	// useEffect(() => {
	// 	console.log(data);
	// }, [data]);

	useEffect(() => {
		if (editMode !== 'MOVE' && editMode !== 'SELECT') setSelected([]);
	}, [editMode]);

	const isWFDebug = () => {
		try {
			return typeof window !== 'undefined' && (window.__WF_DEBUG === true || window.localStorage?.getItem('WF_DEBUG') === '1');
		} catch (e) {
			return false;
		}
	};
	const wfNames = arr => (Array.isArray(arr) ? arr.map(d => d?.name).filter(n => n != null) : []);
	const wfDiff = (a, b) => a.filter(x => b.indexOf(x) === -1);
	const wfSameSet = (a, b) => wfDiff(a, b).length === 0 && wfDiff(b, a).length === 0;
	const wfDbg = (...args) => { if (isWFDebug()) console.log(...args); };
	const wfGroup = (label, fn) => {
		if (!isWFDebug()) return;
		console.groupCollapsed(label);
		try { fn(); } finally { console.groupEnd(); }
	};

	
	const isWFFuzzy = () => {
		try {
			return typeof window !== 'undefined' && (window.__WF_FUZZY === true || window.localStorage?.getItem('WF_FUZZY') === '1');
		} catch (e) {
			return false;
		}
	};

	// v7: banner to verify patched provider is active (prints once when WF_DEBUG is on)
	useEffect(() => {
		if (typeof window === 'undefined') return;
		let debugOn = false;
		let fuzzyOn = false;
		try {
			debugOn = (window.__WF_DEBUG === true) || (window.localStorage?.getItem('WF_DEBUG') === '1');
			fuzzyOn = (window.__WF_FUZZY === true) || (window.localStorage?.getItem('WF_FUZZY') === '1');
		} catch (e) { /* noop */ }
		if (!debugOn) return;
		if (window.__WF_PROVIDER_V7_LOADED) return;
		window.__WF_PROVIDER_V7_LOADED = true;
		console.info('[WF] WorkflowProvider v8 loaded', { debugOn: true, fuzzyOn });
	}, []);

	const wfCanonKey = (value) => {
		const s = String(value ?? '').trim().toLowerCase();
		if (!s) return '';
		// Normalize: remove common separators and punctuation, keep letters+numbers
		try {
			return s.normalize('NFKD')
				.replace(/[\s_\-]+/g, '')
				.replace(/[^\p{L}\p{N}]+/gu, '');
		} catch (e) {
			// Fallback for environments without Unicode property escapes
			return s.replace(/[\s_\-]+/g, '').replace(/[^a-z0-9]+/g, '');
		}
	};

	const wfLevenshtein = (a, b) => {
		if (a === b) return 0;
		const al = a.length, bl = b.length;
		if (!al) return bl;
		if (!bl) return al;

		const prev = new Array(bl + 1);
		const curr = new Array(bl + 1);
		for (let j = 0; j <= bl; j++) prev[j] = j;

		for (let i = 1; i <= al; i++) {
			curr[0] = i;
			const ca = a.charCodeAt(i - 1);
			for (let j = 1; j <= bl; j++) {
				const cb = b.charCodeAt(j - 1);
				const cost = ca === cb ? 0 : 1;
				const del = prev[j] + 1;
				const ins = curr[j - 1] + 1;
				const sub = prev[j - 1] + cost;
				curr[j] = del < ins ? (del < sub ? del : sub) : (ins < sub ? ins : sub);
			}
			for (let j = 0; j <= bl; j++) prev[j] = curr[j];
		}
		return prev[bl];
	};

	const wfSimilarity = (a, b) => {
		const al = a.length, bl = b.length;
		const maxLen = Math.max(al, bl, 1);
		const dist = wfLevenshtein(a, b);
		return 1 - (dist / maxLen);
	};

	const wfSuggestMapKeys = (map, rawKey, limit = 5) => {
		if (!map || rawKey === null || rawKey === undefined) return [];
		const keys = Object.keys(map);
		const rawCanon = wfCanonKey(rawKey);
		if (!rawCanon) return [];

		const scored = keys.map(k => {
			const kc = wfCanonKey(k);
			let score = 0;
			if (kc === rawCanon) score = 1;
			else if (kc && (rawCanon.includes(kc) || kc.includes(rawCanon))) score = 0.93 + (Math.min(kc.length, rawCanon.length) / Math.max(kc.length, rawCanon.length)) * 0.05;
			else score = wfSimilarity(rawCanon, kc);
			return { key: k, mapped: map[k], score };
		}).sort((a, b) => b.score - a.score);

		return scored.slice(0, limit);
	};

	const wfResolveMapKey = (map, rawKey, opts = {}) => {
		if (!map || rawKey === null || rawKey === undefined) return null;

		const raw = String(rawKey);
		if (Object.prototype.hasOwnProperty.call(map, raw)) {
			return { lookupKey: raw, mapped: map[raw], rule: 'exact', score: 1 };
		}

		const keys = Object.keys(map);
		const rawCanon = wfCanonKey(raw);

		// Canonical exact match (unique)
		if (rawCanon) {
			let canonMatchKey = null;
			let canonMatchCount = 0;
			for (let i = 0; i < keys.length; i++) {
				const k = keys[i];
				if (wfCanonKey(k) === rawCanon) { canonMatchKey = k; canonMatchCount++; }
			}
			if (canonMatchCount === 1 && canonMatchKey != null) {
				return { lookupKey: canonMatchKey, mapped: map[canonMatchKey], rule: 'canonExact', score: 1 };
			}
		}

		// Contains-based unique match (safe-ish)
		if (rawCanon) {
			const contains = [];
			for (let i = 0; i < keys.length; i++) {
				const k = keys[i];
				const kc = wfCanonKey(k);
				if (!kc) continue;
				if (rawCanon.includes(kc) || kc.includes(rawCanon)) {
					const ratio = Math.min(kc.length, rawCanon.length) / Math.max(kc.length, rawCanon.length);
					contains.push({ key: k, score: 0.93 + ratio * 0.05 });
				}
			}
			contains.sort((a, b) => b.score - a.score);
			if (contains.length === 1) {
				const k = contains[0].key;
				return { lookupKey: k, mapped: map[k], rule: 'containsUnique', score: contains[0].score };
			}
			if (contains.length > 1) {
				const top = contains[0], second = contains[1];
				const minGap = Number.isFinite(opts.minGap) ? opts.minGap : 0.05;
				if ((top.score - second.score) >= minGap) {
					return { lookupKey: top.key, mapped: map[top.key], rule: 'containsGap', score: top.score };
				}
			}
		}

		// Fuzzy match (ONLY if explicitly enabled)
		const allowFuzzy = opts.allowFuzzy === true;
		if (allowFuzzy && rawCanon) {
			const scored = [];
			for (let i = 0; i < keys.length; i++) {
				const k = keys[i];
				const kc = wfCanonKey(k);
				if (!kc) continue;
				const score = wfSimilarity(rawCanon, kc);
				scored.push({ key: k, score });
			}
			scored.sort((a, b) => b.score - a.score);
			const top = scored[0];
			const second = scored[1];

			const minScore = Number.isFinite(opts.minScore) ? opts.minScore : 0.92;
			const minGap = Number.isFinite(opts.minGap) ? opts.minGap : 0.05;

			if (top && top.score >= minScore && (!second || (top.score - second.score) >= minGap)) {
				return { lookupKey: top.key, mapped: map[top.key], rule: 'fuzzy', score: top.score };
			}
		}

		return null;
	};

	// ---- Value minified sync (UI only; backend payload unchanged) ----
	// NOTE: WorkflowValue uses these exact sizes
	const WF_VALUE_WIDTH = 150;
	const WF_VALUE_MINIFIED_WIDTH = 50;
	const WF_VALUE_ANCHOR_PAD = 10; // circle is drawn 10px outside the box edge

	const wfValueHalfW = (v) => ((v && v.minified) ? WF_VALUE_MINIFIED_WIDTH : WF_VALUE_WIDTH) / 2;
	const wfValueAnchorPos = (v) => {
		if (!v) return null;
		const half = wfValueHalfW(v);
		const x = v.io === 'OUTPUT' ? (v.x - half - WF_VALUE_ANCHOR_PAD) : (v.x + half + WF_VALUE_ANCHOR_PAD);
		return { x, y: v.y };
	};

	// Recompute VALUE.minified based on connected TRANSFORM pin minified flags.
	// - If a VALUE is connected to >=1 pin: minified = ANY(connected pin.minified === true)
	// - If not connected: keep current minified (do not surprise the user)
	// Also re-anchors line endpoints for VALUEs whose minified changed (box width changes).
	const wfSyncValueMinifiedFromPins = (_data, debugLabel = '') => {
		if (!_data || !Array.isArray(_data.value) || !Array.isArray(_data.line) || !Array.isArray(_data.transform)) return _data;

		const resolveField = (fields, rawKey) => {
			if (!Array.isArray(fields) || rawKey === null || rawKey === undefined) return null;

			const fieldByName = {};
			for (let i = 0; i < fields.length; i++) {
				const f = fields[i];
				if (f && f.name != null) fieldByName[String(f.name)] = f;
			}

			const raw = String(rawKey);
			if (Object.prototype.hasOwnProperty.call(fieldByName, raw)) return fieldByName[raw];

			// Identity map so we can reuse wfResolveMapKey's canonical/contains/fuzzy logic
			const nameMap = {};
			const keys = Object.keys(fieldByName);
			for (let i = 0; i < keys.length; i++) nameMap[keys[i]] = keys[i];

			const res = wfResolveMapKey(nameMap, raw, { allowFuzzy: isWFFuzzy(), minScore: 0.92, minGap: 0.05 });
			return res ? fieldByName[res.mapped] : null;
		};


		const valByKey = new Map(_data.value.map(v => [v.key, v]));
		const trByKey = new Map(_data.transform.map(tr => [tr.key, tr]));

		// valueKey -> list of booleans (pin.minified)
		const pinFlagsByValueKey = new Map();

		const pushFlag = (valueKey, flag) => {
			if (!valueKey) return;
			const arr = pinFlagsByValueKey.get(valueKey) || [];
			arr.push(!!flag);
			pinFlagsByValueKey.set(valueKey, arr);
		};

		for (let i = 0; i < _data.line.length; i++) {
			const line = _data.line[i];
			if (!line || !line.start || !line.end) continue;

			const startValue = valByKey.get(line.start.key);
			const endValue = valByKey.get(line.end.key);
			const startTr = trByKey.get(line.start.key);
			const endTr = trByKey.get(line.end.key);

			// value -> transform input
			if (startValue && endTr) {
				const field = resolveField(endTr.input_fields, line.end.valueKey);
				if (field) pushFlag(startValue.key, field.minified);
			}
			// transform output -> value
			else if (endValue && startTr) {
				const field = resolveField(startTr.output_fields, line.start.valueKey);
				if (field) pushFlag(endValue.key, field.minified);
			}
		}

		let changedCount = 0;
		const changedKeys = new Set();
		const changes = [];

		const newValues = _data.value.map(v => {
			const flags = pinFlagsByValueKey.get(v.key);
			if (!flags || flags.length === 0) return v;

			const nextMin = flags.some(Boolean);
			if (!!v.minified === !!nextMin) return v;

			// Keep the *pin* (anchor) position stable when the box width changes:
			// - OUTPUT value boxes have the pin on the LEFT -> expand/shrink to the RIGHT
			// - INPUT value boxes have the pin on the RIGHT -> expand/shrink to the LEFT
			const oldHalf = wfValueHalfW(v);
			const nextHalf = ((nextMin ? WF_VALUE_MINIFIED_WIDTH : WF_VALUE_WIDTH) / 2);
			const deltaHalf = nextHalf - oldHalf;
			const shiftX = (deltaHalf !== 0)
				? (v.io === 'OUTPUT' ? deltaHalf : -deltaHalf)
				: 0;

			changedCount++;
			changedKeys.add(v.key);
			changes.push({ key: v.key, io: v.io, valueKey: v.valueKey, from: !!v.minified, to: !!nextMin, connectedPins: flags.length, shiftX });

			return { ...v, minified: nextMin, x: v.x + shiftX };
		});

		if (changedCount === 0) { wfDbg(`[WF] sync VALUE.minified from pins ${debugLabel}: no changes`); return _data; }

		const newValByKey = new Map(newValues.map(v => [v.key, v]));
		const newLines = _data.line.map(l => {
			let out = l;
			if (changedKeys.has(l.start?.key)) {
				const v = newValByKey.get(l.start.key);
				const p = wfValueAnchorPos(v);
				if (p) out = { ...out, start: { ...out.start, pos: p } };
			}
			if (changedKeys.has(l.end?.key)) {
				const v = newValByKey.get(l.end.key);
				const p = wfValueAnchorPos(v);
				if (p) out = { ...out, end: { ...out.end, pos: p } };
			}
			return out;
		});

		wfGroup(`[WF] sync VALUE.minified from pins ${debugLabel} (changed=${changedCount})`, () => {
			console.table(changes.slice(0, 50));
			wfDbg('note', 'VALUE.minified is derived from connected pin.minified (ANY true). Unconnected values keep their previous minified.');
		});

		return { ..._data, value: newValues, line: newLines };
	};


	const prevLinesRef = useRef(null);

	useEffect(() => {
		if (!isWFDebug()) return;
		if (!data || !Array.isArray(data.line)) { prevLinesRef.current = null; return; }

		const prev = prevLinesRef.current;
		if (prev && Array.isArray(prev) && data.line.length < prev.length) {
			const currKeys = new Set(data.line.map(l => l.key));
			const removed = prev.filter(l => !currKeys.has(l.key));
			if (removed.length > 0) {
				wfGroup(`[WF] lines removed: ${removed.length}`, () => {
					console.table(removed.map(l => ({
						line: l.key,
						startKey: l.start?.key,
						startValueKey: l.start?.valueKey,
						endKey: l.end?.key,
						endValueKey: l.end?.valueKey
					})));
				});
			}
		}

		// store a shallow clone (positions might be mutated elsewhere)
		prevLinesRef.current = data.line.map(l => ({
			key: l.key,
			start: l.start ? { ...l.start, pos: l.start.pos ? { ...l.start.pos } : l.start.pos } : l.start,
			end: l.end ? { ...l.end, pos: l.end.pos ? { ...l.end.pos } : l.end.pos } : l.end
		}));
	}, [data]);

	const checkStageCorner = () => {
		const mousePos = stage.getPointerPosition();
		const stageWidth = stage.width();
		const stageHeight = stage.height();
		const horizontalSensitivity = stageWidth * (cornerPercent / 100);
		const verticalSensitivity = stageHeight * (cornerPercent / 100);
		if ((mousePos.y <= slideSensitivity && mousePos.x <= horizontalSensitivity) || (mousePos.x <= slideSensitivity && mousePos.y <= verticalSensitivity)) {
			// TOPLEFT
			setData({ ...data, stage: { ...data.stage, x: data.stage.x + jumpSensitivity, y: data.stage.y + jumpSensitivity } });
		} else if ((mousePos.x >= stageWidth - horizontalSensitivity && mousePos.y <= slideSensitivity) || (mousePos.x >= stageWidth - slideSensitivity && mousePos.y <= verticalSensitivity)) {
			// TOPRIGHT
			setData({ ...data, stage: { ...data.stage, x: data.stage.x - jumpSensitivity, y: data.stage.y + jumpSensitivity } });
		} else if ((mousePos.x <= slideSensitivity && mousePos.y >= stageHeight - verticalSensitivity) || (mousePos.x <= horizontalSensitivity && mousePos.y >= stageHeight - slideSensitivity)) {
			// BOTTOMLEFT
			setData({ ...data, stage: { ...data.stage, x: data.stage.x + jumpSensitivity, y: data.stage.y - jumpSensitivity } });
		} else if ((mousePos.x >= stageWidth - horizontalSensitivity && mousePos.y >= stageHeight - slideSensitivity) || (mousePos.x >= stageWidth - slideSensitivity && mousePos.y >= stageHeight - verticalSensitivity)) {
			// BOTTOMRIGHT
			setData({ ...data, stage: { ...data.stage, x: data.stage.x - jumpSensitivity, y: data.stage.y - jumpSensitivity } });
		} else if (mousePos.x <= slideSensitivity) {
			// LEFT
			setData({ ...data, stage: { ...data.stage, x: data.stage.x + jumpSensitivity } });
		} else if (mousePos.x >= stageWidth - slideSensitivity) {
			// RIGHT
			setData({ ...data, stage: { ...data.stage, x: data.stage.x - jumpSensitivity } });
		} else if (mousePos.y <= slideSensitivity) {
			// TOP
			setData({ ...data, stage: { ...data.stage, y: data.stage.y + jumpSensitivity } });
		} else if (mousePos.y >= stageHeight - slideSensitivity) {
			// BOTTOM
			setData({ ...data, stage: { ...data.stage, y: data.stage.y - jumpSensitivity } });
		}
	};

	const initLine = (startKey, startValueKey, startPos) => {
		const newLine = {
			key: generateId(),
			start: { key: startKey, valueKey: startValueKey, pos: startPos },
			end: {
				key: null,
				valueKey: null,
				pos: startPos
			}
		};

		setData({
			...data,
			line: [...data.line, newLine]
		});
		setUnderEditing(newLine);
	};

	const drawLine = (x, y) => {
		const line = {
			...underEditing,
			end: { ...underEditing.end, pos: { x, y } }
		};
		setUnderEditing(line);
		setData({
			...data,
			line: data.line.map(d => {
				if (d.key === line.key) return line;
				return d;
			})
		});
	};

	const endLine = (itemKey, name, pointPos) => {
		if (data.line.findIndex(d => d.end.key === itemKey && d.end.valueKey === name) > -1) {
			showMessage(t('lineAlreadyConnected'));
			setData({
				...data,
				line: data.line.filter(d => d.key !== underEditing.key)
			});
			setUnderEditing(null);
		} else {
			const line = {
				...underEditing,
				end: { ...underEditing.end, key: itemKey, valueKey: name, pos: pointPos }
			};
			setUnderEditing(null);
			setData({
				...data,
				line: data.line.map(d => {
					if (d.key === line.key) return line;
					return d;
				})
			});
		}
	};

	/*
	const endLine = (itemKey, multipleEndInput) => {
		const { valueKey } = underEditing.start;
		if (
			data.line.findIndex(d => d.end.key === itemKey && d.end.valueKey === valueKey) > -1 ||
			data.transform.findIndex(d => d.key === itemKey && ((!multipleEndInput && d.inputs.length > 0) || (multipleEndInput && d.inputs.findIndex(inKey => inKey === valueKey) > -1))) > -1
		) {
			dispatch(showMessage({ message: t('lineAlreadyConnected') }));
			setData({
				...data,
				line: data.line.filter(d => d.key !== underEditing.key)
			});
			setUnderEditing(null);
		} else {
			const line = {
				...underEditing,
				end: { ...underEditing.end, key: itemKey, valueKey }
			};
			setUnderEditing(null);
			setData({
				...data,
				line: data.line.map(d => {
					if (d.key === line.key) return line;
					return d;
				}),
				transform: data.transform.map(d => {
					if (d.key === itemKey) {
						if (_.isUndefined(d.definition.input_keys))
							return {
								...d,
								inputs: [...d.inputs, valueKey],
								definition: {
									...d.definition,
									input_key: multipleEndInput ? d.definition.input_key : valueKey
								}
							};
						return {
							...d,
							inputs: [...d.inputs, valueKey],
							definition: {
								...d.definition,
								input_keys: [...d.inputs, valueKey]
							}
						};
					}
					return d;
				})
			});
		}
	};
*/
	const updateLine = (itemKey, valueKey, pos) => {
		setData({
			...data,
			line: data.line.map(d => {
				return {
					...d,
					start:
						d.start.key === itemKey && d.start.valueKey === valueKey
							? {
								...d.start,
								pos
							}
							: d.start,
					end:
						d.end.key === itemKey && d.end.valueKey === valueKey
							? {
								...d.end,
								pos
							}
							: d.end
				};
			})
		});
	};

	const updateLines = changedLines => {
		const _data = { ...data };
		changedLines.forEach(_line => {
			_data.line.forEach(_l => {
				if (_l.start.key === _line.itemKey && _l.start.valueKey === _line.valueKey) {
					_l.start.pos = _line.pos;
				}
				if (_l.end.key === _line.itemKey && _l.end.valueKey === _line.valueKey) {
					_l.end.pos = _line.pos;
				}
			});
		});
		setData(_data);
	};

	const deleteLine = lineKey => {
		setData({
			...data,
			line: data.line.filter(d => d.key !== lineKey)
		});
	};

	const valueKeyAvailable = (valueKey, itemKey) => {
		console.log(data);
		console.log(data.value);
		let found = false;
		data.value.forEach(d => {
			if ((itemKey == null || d.key !== itemKey) && valueKey === d.valueKey) found = true;
		});
		return !found;
	};

	const valueKeysAvailable = (valueList, itemKey) => {
		let found = false;
		data.value.forEach(d => {
			if (d.key !== itemKey && valueList.indexOf(d.valueKey) > -1) found = true;
		});
		data.transform.forEach(d => {
			if (d.key !== itemKey) {
				d.outputs.forEach(output => {
					if (valueList.indexOf(output) > -1) found = true;
				});
			}
		});
		return !found;
	};

	const outputKeyNameChanged = (_data, oldName, newName) => {
		setData({
			..._data,
			line: _data.line.map(l => {
				if (l.start.valueKey === oldName) {
					return {
						...l,
						start: {
							...l.start,
							valueKey: newName
						},
						end: {
							...l.end,
							valueKey: newName
						}
					};
				}
				return l;
			}),
			transform: _data.transform.map(tr => {
				if (tr.inputs.findIndex(input => input === oldName) > -1) {
					return {
						...tr,
						inputs: tr.inputs.map(input => (input === oldName ? newName : input))
					};
				}
				if (tr.definition.input_key && tr.definition.input_key === oldName) {
					return {
						...tr,
						definition: { ...tr.definition, input_key: newName }
					};
				}
				if (tr.definition.input_keys && tr.definition.input_keys.findIndex(input => input === oldName) > -1) {
					return {
						...tr,
						definition: { ...tr.definition, input_keys: tr.definition.input_keys.map(input => (input === oldName ? newName : input)) }
					};
				}
				return tr;
			})
		});
	};

	const transformationNameAvailable = (valueKey, itemKey) => {
		return data.transform.findIndex(d => d.key !== itemKey && d.name === valueKey) === -1;
	};


	const updateTransformationProperties = (item, value) => {
		const oldTr = item;
		const newTr = value;

		if (newTr && newTr.new) delete newTr.new;

		const inputFieldMap = {};
		const outputFieldMap = {};

		const oldInputList = wfNames(oldTr?.input_fields);
		const newInputList = wfNames(newTr?.input_fields);

		if (newInputList.length === oldInputList.length) {
			oldInputList.forEach((oldKey, idx) => { inputFieldMap[oldKey] = newInputList[idx]; });
		} else {
			oldInputList.forEach(oldKey => { if (newInputList.indexOf(oldKey) > -1) inputFieldMap[oldKey] = oldKey; });
		}

		const oldOutputList = wfNames(oldTr?.output_fields);
		const newOutputList = wfNames(newTr?.output_fields);

		if (newOutputList.length === oldOutputList.length) {
			oldOutputList.forEach((oldKey, idx) => { outputFieldMap[oldKey] = newOutputList[idx]; });
		} else {
			// ✅ FIX: indexOf must be compared to -1
			oldOutputList.forEach(oldKey => { if (newOutputList.indexOf(oldKey) > -1) outputFieldMap[oldKey] = oldKey; });
		}

		wfGroup(`[WF] updateTransformationProperties ${oldTr?.key} (${oldTr?.name || ''})`, () => {
			wfDbg('minified', { before: oldTr?.minified, after: newTr?.minified });
			wfDbg('input_fields', { beforeLen: oldInputList.length, afterLen: newInputList.length });
			wfDbg('output_fields', { beforeLen: oldOutputList.length, afterLen: newOutputList.length });

			if (newOutputList.length === oldOutputList.length && wfSameSet(oldOutputList, newOutputList) && oldOutputList.join('|') !== newOutputList.join('|')) {
				wfDbg('⚠️ output_fields order changed (same set, different order). Index-based mapping will remap keys.');
			}
			if (newInputList.length === oldInputList.length && wfSameSet(oldInputList, newInputList) && oldInputList.join('|') !== newInputList.join('|')) {
				wfDbg('⚠️ input_fields order changed (same set, different order). Index-based mapping will remap keys.');
			}

			wfDbg('missingFromResponse.output', wfDiff(oldOutputList, newOutputList));
			wfDbg('newInResponse.output', wfDiff(newOutputList, oldOutputList));
			wfDbg('missingFromResponse.input', wfDiff(oldInputList, newInputList));
			wfDbg('newInResponse.input', wfDiff(newInputList, oldInputList));
			wfDbg('fuzzyEnabled', isWFFuzzy());
		});

		const beforeLines = Array.isArray(data?.line) ? data.line : [];
		const related = beforeLines.filter(l => l?.start?.key === oldTr?.key || l?.end?.key === oldTr?.key);

		const unresolved = [];
		const normalized = [];

		const resolveOpts = { allowFuzzy: isWFFuzzy(), minScore: 0.92, minGap: 0.05 };

		const newLines = beforeLines.map(line => {
			// incoming to this transform: VALUE -> TRANSFORM (end is transform)
			if (line?.end?.key === oldTr?.key) {
				const res = wfResolveMapKey(inputFieldMap, line.end.valueKey, resolveOpts);
				if (!res) {
					unresolved.push({
						line: line.key,
						side: 'end',
						from: line.end.valueKey,
						suggestions: wfSuggestMapKeys(inputFieldMap, line.end.valueKey, 3).map(s => `${s.key} (${Math.round(s.score * 100)}%)`).join(', ')
					});
					return line; // ✅ IMPORTANT: keep the line (do NOT drop)
				}
				if (res.lookupKey !== String(line.end.valueKey) || res.mapped !== line.end.valueKey) {
					normalized.push({ line: line.key, side: 'end', from: line.end.valueKey, lookupKey: res.lookupKey, mapped: res.mapped, rule: res.rule, score: res.score });
				}
				return { ...line, end: { ...line.end, valueKey: res.mapped } };
			}

			// outgoing from this transform: TRANSFORM -> VALUE (start is transform)
			if (line?.start?.key === oldTr?.key) {
				const res = wfResolveMapKey(outputFieldMap, line.start.valueKey, resolveOpts);
				if (!res) {
					unresolved.push({
						line: line.key,
						side: 'start',
						from: line.start.valueKey,
						suggestions: wfSuggestMapKeys(outputFieldMap, line.start.valueKey, 3).map(s => `${s.key} (${Math.round(s.score * 100)}%)`).join(', ')
					});
					return line; // ✅ IMPORTANT: keep the line (do NOT drop)
				}
				if (res.lookupKey !== String(line.start.valueKey) || res.mapped !== line.start.valueKey) {
					normalized.push({ line: line.key, side: 'start', from: line.start.valueKey, lookupKey: res.lookupKey, mapped: res.mapped, rule: res.rule, score: res.score });
				}
				return { ...line, start: { ...line.start, valueKey: res.mapped } };
			}

			return line;
		});

		wfGroup(`[WF] lines for ${oldTr?.key}: before=${beforeLines.length} after=${newLines.length} related=${related.length} unresolved=${unresolved.length}`, () => {
			if (unresolved.length > 0) {
				console.table(unresolved.slice(0, 50));
				wfDbg('⚠️ unresolved lines were kept (not dropped). If you later export/save, backend may reject unknown pin names.');
			} else {
				wfDbg('no unresolved lines in this update');
			}
			if (normalized.length > 0) {
				wfGroup(`[WF] normalized keys: ${normalized.length}`, () => { console.table(normalized.slice(0, 50)); });
			}
		});

		const baseData = {
			...data,
			line: newLines,
			transform: data.transform.map(c => (c.key === oldTr.key ? newTr : c))
		};

		// Sync VALUE.minified (UI) based on connected pin minified flags, and re-anchor line endpoints if needed
		const synced = wfSyncValueMinifiedFromPins(baseData, `after updateTransformationProperties ${oldTr?.key}`);

		setData(synced);
	};


	const updateIOProperties = (item, value) => {
		wfGroup(`[WF] updateIOProperties ${item?.key}`, () => {
			wfDbg('before', item);
			wfDbg('after', value);
		});

		if (value && value.new) delete value.new;

		if (item.io !== value.io) {
			setData({
				...data,
				line: data.line.filter(line => {
					if (line.end.key === item.key) return false;
					if (line.start.key === item.key) return false;
					return true;
				}),
				value: data.value.map(c => (c.key === item.key ? value : c))
			});
		} else if (item.valueKey !== value.valueKey) {
			setData({
				...data,
				line: data.line.map(line => {
					if (item.io === 'INPUT' && line.start.key === item.key) return { ...line, start: { ...line.start, valueKey: value.valueKey } };
					if (item.io === 'OUTPUT' && line.end.key === item.key) return { ...line, end: { ...line.end, valueKey: value.valueKey } };
					return line;
				}),
				value: data.value.map(c => (c.key === item.key ? value : c))
			});
		} else {
			setData({
				...data,
				value: data.value.map(c => (c.key === item.key ? value : c))
			});
		}
	};


	const deleteNewCanceledTransformation = item => {
		if (item.new) {
			setData({
				...data,
				transform: data.transform.filter(d => d.key !== item.key)
			});
		}
	};

	const showMessage = message => {
		dispatch(sliceShowMessage({ message }));
	};

	const value = {
		stage,
		setStage,
		data,
		setData,
		editMode,
		setEditMode,
		showPropertiesDialog,
		setShowPropertiesDialog,
		checkStageCorner,
		underEditing,
		setUnderEditing,
		initLine,
		drawLine,
		endLine,
		updateLine,
		updateLines,
		deleteLine,
		valueKeyAvailable,
		valueKeysAvailable,
		outputKeyNameChanged,
		selected,
		setSelected,
		calculate,
		setCalculate,
		selectedCalculate,
		setSelectedCalculate,
		floatCalculate,
		setFloatCalculate,
		isTransformer,
		transformationNameAvailable,
		updateTransformationProperties,
		updateIOProperties,
		deleteNewCanceledTransformation,
		floatValidation,
		setFloatValidation,
		showMessage
	};

	return <WorkflowContext.Provider value={value}>{props.children}</WorkflowContext.Provider>;
}
